"use client"

import { useLocale } from "next-intl"
import { useEffect } from "react"
import { readLocaleCookie, writeLocaleCookie } from "./localeCookie"
import { isSupportedLocale } from "./locales"

/**
 * Keeps `<html lang>` in sync with the active locale, for accessibility (screen
 * readers announce content in the right language), and mirrors that locale into
 * the `siro-locale` cookie.
 *
 * The root layout renders `<html lang="en">` statically. The locale is resolved
 * per-request from the viewer's user record, but `<html>` sits above every
 * Suspense boundary, so reading the dynamic locale to set `lang` server-side
 * would break the Cache Components static shell. Setting it on the client after
 * hydration is the deliberate trade-off — same client-island pattern as
 * `TimeZoneProvider`.
 *
 * The cookie write is what lets `request.ts` skip its profile fetch on every
 * load after the first (see the precedence comment there). Writing it from the
 * browser rather than through a Server Action keeps it off the request path
 * entirely — it's a UI preference, not a credential.
 */
export const LocaleSync = () => {
  const locale = useLocale()

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  useEffect(() => {
    // Write only on drift, so the steady state touches nothing.
    if (!isSupportedLocale(locale) || readLocaleCookie() === locale) return
    writeLocaleCookie(locale)
  }, [locale])

  return null
}
